import React, { useEffect, useRef, useState, useCallback } from "react";
import styles from "./Home.module.css"; // Import CSS Module
import Faqs from "./Faqs";
import HowToUse from "./HowToUse";
import HowToCompress from "./HowToCompress";
import ConversionFeatures from "./ConversionFeatures";
import HowSecured from "./HowSecured";
import AboutPage from "./AboutPage";
import Features from "./Features";

export const ImageCompressor = () => {
  const [originalImage, setOriginalImage] = useState(null);
  const [compressedImage, setCompressedImage] = useState(null);
  const [fileName, setFileName] = useState("");
  const [quality, setQuality] = useState(80);
  const [isProcessing, setIsProcessing] = useState(false);
  const [originalSize, setOriginalSize] = useState(0);
  const [compressedSize, setCompressedSize] = useState(0);
  const [error, setError] = useState("");
  const [isDragging, setIsDragging] = useState(false);
  const [needsRecompress, setNeedsRecompress] = useState(false);

  // Resize states
  const [originalDimensions, setOriginalDimensions] = useState({
    width: 0,
    height: 0,
  });
  const [resizeWidth, setResizeWidth] = useState("");
  const [resizeHeight, setResizeHeight] = useState("");
  const [maintainAspectRatio, setMaintainAspectRatio] = useState(true);

  const fileInputRef = useRef(null);
  const canvasRef = useRef(null);

  // Handle drag and drop events
  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const files = e.dataTransfer.files;
    if (files.length > 0) {
      handleImageFile(files[0]);
    }
  }, []);

  const handleFileInput = useCallback((e) => {
    const file = e.target.files[0];
    if (file) {
      handleImageFile(file);
    }
  }, []);

  const handleImageFile = useCallback((file) => {
    if (!file.type.startsWith("image/")) {
      setError("Please select an image file");
      return;
    }

    setError("");
    setFileName(file.name.replace(/\.[^/.]+$/, ""));
    setOriginalSize(file.size);
    setCompressedSize(0);
    setCompressedImage(null);
    setNeedsRecompress(false);
    setResizeWidth("");
    setResizeHeight("");

    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new Image();
      img.onload = () => {
        setOriginalDimensions({
          width: img.width,
          height: img.height,
        });
        setOriginalImage(e.target.result);
      };
      img.onerror = () => {
        setError("Failed to load image dimensions");
      };
      img.src = e.target.result;
    };
    reader.onerror = () => {
      setError("Failed to read image file");
    };
    reader.readAsDataURL(file);
  }, []);

  // Calculate dimensions based on aspect ratio
  const calculateDimensions = useCallback(
    (width, height) => {
      if (
        !maintainAspectRatio ||
        !originalDimensions.width ||
        !originalDimensions.height
      ) {
        return { width, height };
      }

      const aspectRatio = originalDimensions.width / originalDimensions.height;

      if (width && !height) {
        return {
          width: parseInt(width),
          height: Math.round(width / aspectRatio),
        };
      } else if (height && !width) {
        return {
          width: Math.round(height * aspectRatio),
          height: parseInt(height),
        };
      } else if (width && height) {
        return { width: parseInt(width), height: parseInt(height) };
      }

      return {
        width: originalDimensions.width,
        height: originalDimensions.height,
      };
    },
    [maintainAspectRatio, originalDimensions]
  );

  const compressImage = useCallback(async () => {
    if (!originalImage) return;

    setIsProcessing(true);
    setError("");

    try {
      // Create image element
      const img = new Image();

      await new Promise((resolve, reject) => {
        img.onload = resolve;
        img.onerror = () => reject(new Error("Failed to load image"));
        img.src = originalImage;
      });

      // Get canvas context
      const canvas = canvasRef.current;
      const ctx = canvas.getContext("2d");

      // Calculate final dimensions
      const finalDimensions = calculateDimensions(resizeWidth, resizeHeight);
      const width = finalDimensions.width || img.width;
      const height = finalDimensions.height || img.height;

      // Set canvas dimensions
      canvas.width = width;
      canvas.height = height;

      // Draw image on canvas with resizing
      ctx.drawImage(img, 0, 0, width, height);

      // Convert to JPEG with quality
      canvas.toBlob(
        (blob) => {
          if (!blob) {
            throw new Error("Compression failed");
          }

          const compressedUrl = URL.createObjectURL(blob);
          setCompressedImage(compressedUrl);
          setCompressedSize(blob.size);
          setNeedsRecompress(false);
          setIsProcessing(false);
        },
        "image/jpeg",
        quality / 100
      );
    } catch (err) {
      setError(err.message);
      setIsProcessing(false);
    }
  }, [originalImage, quality, resizeWidth, resizeHeight, calculateDimensions]);

  const downloadImage = useCallback(() => {
    if (!compressedImage) return;

    const finalDimensions = calculateDimensions(resizeWidth, resizeHeight);
    const width = finalDimensions.width || originalDimensions.width;
    const height = finalDimensions.height || originalDimensions.height;

    const link = document.createElement("a");
    link.href = compressedImage;
    link.download = `${fileName}_compressed_${width}x${height}_q${quality}.jpg`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }, [
    compressedImage,
    fileName,
    quality,
    resizeWidth,
    resizeHeight,
    calculateDimensions,
    originalDimensions,
  ]);

  const resetAll = useCallback(() => {
    setOriginalImage(null);
    setCompressedImage(null);
    setFileName("");
    setQuality(80);
    setOriginalSize(0);
    setCompressedSize(0);
    setError("");
    setNeedsRecompress(false);
    setOriginalDimensions({ width: 0, height: 0 });
    setResizeWidth("");
    setResizeHeight("");

    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  }, []);

  const handleQualityChange = useCallback(
    (newQuality) => {
      setQuality(newQuality);
      if (compressedImage) {
        setNeedsRecompress(true);
      }
    },
    [compressedImage]
  );

  const handleWidthChange = useCallback(
    (value) => {
      setResizeWidth(value);
      if (compressedImage) {
        setNeedsRecompress(true);
      }

      if (
        maintainAspectRatio &&
        value &&
        originalDimensions.width &&
        originalDimensions.height
      ) {
        const aspectRatio =
          originalDimensions.width / originalDimensions.height;
        setResizeHeight(Math.round(value / aspectRatio).toString());
      }
    },
    [compressedImage, maintainAspectRatio, originalDimensions]
  );

  const handleHeightChange = useCallback(
    (value) => {
      setResizeHeight(value);
      if (compressedImage) {
        setNeedsRecompress(true);
      }

      if (
        maintainAspectRatio &&
        value &&
        originalDimensions.width &&
        originalDimensions.height
      ) {
        const aspectRatio =
          originalDimensions.width / originalDimensions.height;
        setResizeWidth(Math.round(value * aspectRatio).toString());
      }
    },
    [compressedImage, maintainAspectRatio, originalDimensions]
  );

  const handleAspectRatioChange = useCallback(
    (checked) => {
      setMaintainAspectRatio(checked);
      if (compressedImage) {
        setNeedsRecompress(true);
      }
    },
    [compressedImage]
  );

  const applyPresetSize = useCallback(
    (preset) => {
      setResizeWidth(preset.width.toString());
      setResizeHeight(preset.height.toString());
      if (compressedImage) {
        setNeedsRecompress(true);
      }
    },
    [compressedImage]
  );

  const formatFileSize = useCallback((bytes) => {
    if (!bytes) return "0 B";
    const k = 1024;
    const sizes = ["B", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
  }, []);

  const getCompressionPercentage = useCallback(() => {
    if (originalSize > 0 && compressedSize > 0) {
      return Math.round((1 - compressedSize / originalSize) * 100);
    }
    return 0;
  }, [originalSize, compressedSize]);

  // Preset sizes
  const presetSizes = [
    {
      name: "Original",
      width: originalDimensions.width,
      height: originalDimensions.height,
    },
    { name: "HD (1280x720)", width: 1280, height: 720 },
    { name: "Full HD (1920x1080)", width: 1920, height: 1080 },
    { name: "Instagram Square (1080x1080)", width: 1080, height: 1080 },
    { name: "Instagram Portrait (1080x1350)", width: 1080, height: 1350 },
    { name: "Thumbnail (300x300)", width: 300, height: 300 },
    { name: "Medium (800x600)", width: 800, height: 600 },
  ];

  const finalDimensions = calculateDimensions(resizeWidth, resizeHeight);

  return (
    <div className={styles.container}>
      <h1 className={styles.pageTitle}>Image Compressor & Resizer</h1>

      {error && <div className={styles.errorMessage}>{error}</div>}

      {/* Upload Section */}
      <div className={styles.uploadSection}>
        <div
          className={`${styles.dropArea} ${isDragging ? styles.dragging : ""}`}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          onClick={() => fileInputRef.current?.click()}
        >
          {!originalImage ? (
            <div className={styles.uploadContent}>
              <div className={styles.uploadIcon}>🖼️</div>
              <p>Drag & drop your image here</p>
              <p className={styles.orText}>or</p>
              <button className={styles.browseButton}>Select Image</button>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handleFileInput}
                style={{ display: "none" }}
              />
            </div>
          ) : (
            <div className={styles.imageActions}>
              <button
                className={styles.changeButton}
                onClick={() => fileInputRef.current?.click()}
              >
                Change Image
              </button>
              <button className={styles.resetButton} onClick={resetAll}>
                Reset
              </button>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handleFileInput}
                style={{ display: "none" }}
              />
            </div>
          )}
        </div>
      </div>

      {/* Controls */}
      {originalImage && (
        <div className={styles.controls}>
          {/* Quality Control */}
          <div className={styles.controlGroup}>
            <h3>Compression Settings</h3>
            <div className={styles.qualityControl}>
              <label>Quality: {quality}%</label>
              <input
                type="range"
                min="10"
                max="100"
                value={quality}
                onChange={(e) => handleQualityChange(parseInt(e.target.value))}
                disabled={isProcessing}
              />
              <div className={styles.qualityHint}>
                {quality >= 80
                  ? "High Quality"
                  : quality >= 60
                  ? "Balanced"
                  : quality >= 40
                  ? "Good Compression"
                  : "Maximum Compression"}
              </div>
            </div>
          </div>

          {/* Resize Control */}
          <div className={styles.controlGroup}>
            <h3>Resize Settings</h3>

            {/* Preset Sizes */}
            <div className={styles.presetSizes}>
              <label>Quick Presets:</label>
              <div className={styles.presetButtons}>
                {presetSizes.map((preset, index) => (
                  <button
                    key={index}
                    className={styles.presetButton}
                    onClick={() => applyPresetSize(preset)}
                    disabled={isProcessing}
                  >
                    {preset.name}
                  </button>
                ))}
              </div>
            </div>

            {/* Custom Dimensions */}
            <div className={styles.dimensionControls}>
              <div className={styles.dimensionInputs}>
                <div className={styles.inputGroup}>
                  <label>Width (px)</label>
                  <input
                    type="number"
                    value={resizeWidth}
                    onChange={(e) => handleWidthChange(e.target.value)}
                    placeholder={originalDimensions.width.toString()}
                    min="1"
                    disabled={isProcessing}
                  />
                </div>
                <div className={styles.inputGroup}>
                  <label>Height (px)</label>
                  <input
                    type="number"
                    value={resizeHeight}
                    onChange={(e) => handleHeightChange(e.target.value)}
                    placeholder={originalDimensions.height.toString()}
                    min="1"
                    disabled={isProcessing}
                  />
                </div>
              </div>

              <div className={styles.aspectRatioControl}>
                <label className={styles.checkboxLabel}>
                  <input
                    type="checkbox"
                    checked={maintainAspectRatio}
                    onChange={(e) => handleAspectRatioChange(e.target.checked)}
                    disabled={isProcessing}
                  />
                  Maintain aspect ratio
                </label>
              </div>

              <div className={styles.dimensionInfo}>
                Original: {originalDimensions.width} ×{" "}
                {originalDimensions.height} px
                {resizeWidth || resizeHeight ? (
                  <span className={styles.newDimensions}>
                    → New: {finalDimensions.width} × {finalDimensions.height} px
                  </span>
                ) : null}
              </div>
            </div>
          </div>

          {needsRecompress && (
            <div className={styles.recompressNotice}>
              ⚠️ Settings have changed. Click "Compress Again" to update.
            </div>
          )}

          <button
            className={styles.compressButton}
            onClick={compressImage}
            disabled={isProcessing}
          >
            {isProcessing
              ? "Processing..."
              : needsRecompress
              ? "Compress Again"
              : "Compress Image"}
          </button>
        </div>
      )}

      {/* Preview Section */}
      <div className={styles.previewSection}>
        {originalImage && (
          <div className={styles.previewColumn}>
            <h3>Original Image</h3>
            <div className={styles.imageContainer}>
              <img
                src={originalImage}
                alt="Original"
                className={styles.previewImage}
              />
            </div>
            <div className={styles.fileInfo}>
              <div>Size: {formatFileSize(originalSize)}</div>
              <div>
                Dimensions: {originalDimensions.width} ×{" "}
                {originalDimensions.height} px
              </div>
            </div>
          </div>
        )}

        {compressedImage && (
          <div className={styles.previewColumn}>
            <h3>Compressed Image</h3>
            <div className={styles.imageContainer}>
              <img
                src={compressedImage}
                alt="Compressed"
                className={styles.previewImage}
              />
            </div>
            <div className={styles.fileInfo}>
              <div>Size: {formatFileSize(compressedSize)}</div>
              <div>
                Dimensions: {finalDimensions.width} × {finalDimensions.height}{" "}
                px
              </div>
              <div className={styles.compressionInfo}>
                {getCompressionPercentage()}% smaller
              </div>
              <div className={styles.qualityInfo}>Quality: {quality}%</div>
            </div>
            <button
              className={styles.downloadButton}
              onClick={downloadImage}
              disabled={isProcessing}
            >
              Download Compressed Image
            </button>
          </div>
        )}
      </div>

      {/* Hidden canvas for compression */}
      <canvas ref={canvasRef} style={{ display: "none" }} />
    </div>
  );
};

function Home() {
  return (
    <div>
      <ImageCompressor />
      <AboutPage />
      <HowToUse />
      <HowToCompress />
      <ConversionFeatures />
      <HowSecured />
      <Features />
      <Faqs />
    </div>
  );
}

export default Home;
